import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaChevronDown } from 'react-icons/fa';
import { getPropertyTypes, getMunicipiosYBarrios } from '../services/api';

const SearchBar = () => {
  const navigate = useNavigate();
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [states, setStates] = useState([]);
  const [selectedPropertyType, setSelectedPropertyType] = useState('');
  const [isPropertyDropdownOpen, setIsPropertyDropdownOpen] = useState(false);
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false);
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const [selectedState, setSelectedState] = useState('');
  const [isBarrioDropdownOpen, setIsBarrioDropdownOpen] = useState(false);
  const [selectedBarrio, setSelectedBarrio] = useState('');
  const [barrios, setBarrios] = useState([]);
  // Agregar estos estados debajo de la línea 17
  const [selectedPriceRange, setSelectedPriceRange] = useState(null);
  const [isPriceDropdownOpen, setIsPriceDropdownOpen] = useState(false);


  //const [locationInput, setLocationInput] = useState('');
  //const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);

  // Añadir estilos CSS para el dropdown personalizado
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Dropdown personalizado */
      .custom-dropdown {
        position: relative;
      }
      
      .dropdown-list {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: #0D0D0D;
        border: 2px solid #D4AF37;
        border-top: none;
        border-radius: 0 0 8px 8px;
        max-height: 200px;
        overflow-y: auto;
        z-index: 50;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
      }
      
      .dropdown-option {
        padding: 12px 16px;
        color: #FFFFFF;
        cursor: pointer;
        border-bottom: 1px solid #333;
        transition: all 0.2s ease;
        font-size: 14px;
      }
      
      .dropdown-option:hover {
        background-color: #D4AF37;
        color: #0D0D0D;
      }
      
      .dropdown-option:last-child {
        border-bottom: none;
        border-radius: 0 0 6px 6px;
      }
      
      /* Scrollbar personalizado */
      .dropdown-list::-webkit-scrollbar {
        width: 8px;
      }
      
      .dropdown-list::-webkit-scrollbar-track {
        background: #0D0D0D;
        border-radius: 4px;
      }
      
      .dropdown-list::-webkit-scrollbar-thumb {
        background: #D4AF37;
        border-radius: 4px;
        border: 1px solid #0D0D0D;
      }
      
      .dropdown-list::-webkit-scrollbar-thumb:hover {
        background: #B8941F;
      }
      
      /* Para Firefox */
      .dropdown-list {
        scrollbar-width: thin;
        scrollbar-color: #D4AF37 #0D0D0D;
      }
      
      /* Responsivo */
      @media (max-width: 768px) {
        .dropdown-list {
          max-height: 150px;
        }
        
        .dropdown-option {
          padding: 10px 12px;
          font-size: 13px;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        const [types, locationsData] = await Promise.all([
          getPropertyTypes(),
          getMunicipiosYBarrios()
        ]);
        setPropertyTypes(types);
        setLocations(locationsData);
      } catch (error) {
        console.error('Error fetching initial data:', error.message);
        // Mostrar mensaje de error al usuario
        alert(`Error al cargar los datos: ${error.message}. Por favor, revisa la configuración de la API.`);
        // Set empty arrays if API fails
        setPropertyTypes([]);
        setLocations([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (locations.length > 0) {
      const fetchStates = async () => {
        try {
          //console.log(locations);
          const uniqueStates = [...new Set(locations.map(location => ({municipio: location.municipio, codmunicipio: location.codmunicipio})))];
          //console.log("uniqueStates", uniqueStates);
          setStates(uniqueStates);
        } catch (error) {
          console.error('Error fetching states:', error.message);
          alert(`Error al cargar los estados: ${error.message}. Por favor, revisa la configuración de la API.`);
        }
      };

      fetchStates();
    }
  }, [locations]);

  useEffect(() => {
    if (selectedState) {
      const fetchBarrios = async () => {
        try {
          // 1. Filtramos solo los datos que pertenecen al municipio seleccionado
      const locacionesDelMunicipio = locations.filter(
        location => location.codmunicipio === selectedState.codmunicipio
      );

      // 2. Extraemos los barrios. 
      // Usamos .flatMap() que hace el .map() pero además "aplana" el arreglo 
      // para evitar el problema de los dobles corchetes [ [ ] ].
      const todosLosBarrios = locacionesDelMunicipio.flatMap(location => {
        // Dependiendo de cómo venga tu API, retornamos el arreglo de barrios
        // o construimos el objeto si viene suelto.
        return location.barrios || { barrio: location.barrio, codbarrio: location.codbarrio };
      });

      // 3. Eliminamos duplicados usando Map (la forma correcta para objetos)
      // Usamos el 'codbarrio' como identificador único.
      const barriosUnicos = Array.from(
        new Map(todosLosBarrios.map(b => [b.codbarrio, b])).values()
      );

      // 4. Guardamos los barrios limpios y planos
      setBarrios(barriosUnicos);
        } catch (error) {
          console.error('Error fetching barrios:', error.message);
        }
      };
      fetchBarrios();
    }
  }, [selectedState, locations]);

  // Lógica para los rangos de precio. Agrégalo antes de const handleSearch = () => {
    const getPriceRanges = () => {
      if (selectedServiceType === 'Venta') {
        return [
          { label: "0 - $200'000.000", desde: 0, hasta: 200000000 },
          { label: "$200'000.001 - $300'000.000", desde: 200000001, hasta: 300000000 },
          { label: "$300'000.001 - $400'000.000", desde: 300000001, hasta: 400000000 },
          { label: "Más de $400'000.000", desde: 400000001, hasta: 99999999999 }
        ];
      }
      
      // Por defecto (Arriendo)
      return [
        { label: "0 - $1'500.000", desde: 0, hasta: 1500000 },
        { label: "$1'500.001 - $3'000.000", desde: 1500001, hasta: 3000000 },
        { label: "Más de $3'000.000", desde: 3000001, hasta: 99999999999 }
      ];
    };
  
    const handlePriceSelect = (range) => {
      setSelectedPriceRange(range);
      setIsPriceDropdownOpen(false);
    };
    
    const togglePriceDropdown = () => {
      setIsPriceDropdownOpen(!isPriceDropdownOpen);
      setIsPropertyDropdownOpen(false);
      setIsServiceDropdownOpen(false);
      setIsStateDropdownOpen(false);
      setIsBarrioDropdownOpen(false);
    };

  const handleSearch = () => {
    // Construir parámetros de búsqueda
    const searchParams = new URLSearchParams();
    
    if (selectedServiceType) {
      searchParams.append('destinacion', selectedServiceType);
    }
    
    if (selectedPropertyType) {
      searchParams.append('clase', selectedPropertyType);
    }
    if (selectedState) {
      searchParams.append('municipio', selectedState.municipio);
    }
    if (selectedBarrio) {
      searchParams.append('barrio', selectedBarrio.barrio);
    }
    
    if (selectedPriceRange) {
      searchParams.append('preciodesde', selectedPriceRange.desde);
      searchParams.append('preciohasta', selectedPriceRange.hasta);
  }

    // Navegar a la página de propiedades con los parámetros
    navigate(`/properties?${searchParams.toString()}`);
  };

  const handlePropertyTypeSelect = (type) => {
    setSelectedPropertyType(type);
    setIsPropertyDropdownOpen(false);
  };
  const handleStateSelect = (state) => {
    setSelectedState(state);
    setIsStateDropdownOpen(false);
    setSelectedBarrio('');
  };
  const handleBarrioSelect = (barrio) => {
    setSelectedBarrio(barrio);
    setIsBarrioDropdownOpen(false);
  };

  const togglePropertyDropdown = () => {
    setIsPropertyDropdownOpen(!isPropertyDropdownOpen);
    setIsServiceDropdownOpen(false);
    //setIsLocationDropdownOpen(false);
    setIsPriceDropdownOpen(false);
  };

  const handleServiceTypeSelect = (service) => {
    setSelectedServiceType(service);
    setSelectedPriceRange(null); // Resetea el precio si cambia el servicio
    setIsServiceDropdownOpen(false);
  };

  const toggleServiceDropdown = () => {
    setIsServiceDropdownOpen(!isServiceDropdownOpen);
    setIsPropertyDropdownOpen(false);
    //setIsLocationDropdownOpen(false);
    setIsPriceDropdownOpen(false);
  };

  const toggleStateDropdown = () => {
    setIsStateDropdownOpen(!isStateDropdownOpen);
    setIsPropertyDropdownOpen(false);
    setIsServiceDropdownOpen(false);
    setIsBarrioDropdownOpen(false);
    //setIsLocationDropdownOpen(false);
    setIsPriceDropdownOpen(false);
  };
  const toggleBarrioDropdown = () => {
    setIsBarrioDropdownOpen(!isBarrioDropdownOpen);
    setIsPropertyDropdownOpen(false);
    setIsServiceDropdownOpen(false);
    setIsStateDropdownOpen(false);
    setIsPriceDropdownOpen(false);
  };



  // Cerrar dropdowns cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.custom-dropdown')) {
        setIsPropertyDropdownOpen(false);
        setIsServiceDropdownOpen(false);
        //setIsLocationDropdownOpen(false);
        setIsStateDropdownOpen(false);
        setIsBarrioDropdownOpen(false);
        setIsPriceDropdownOpen(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="bg-brand-black/90 p-6 rounded-lg shadow-2xl w-full backdrop-blur-sm">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-center">
        {/* Dropdown Tipo de Servicio */}
        <div className="custom-dropdown relative md:col-span-1">
          <button
            type="button"
            onClick={toggleServiceDropdown}
            className="w-full p-3 border-2 border-brand-gold rounded-lg bg-brand-black text-brand-white hover:border-brand-gold/80 focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-colors h-12 flex items-center justify-between text-left"
          >
            <span className={selectedServiceType ? 'text-brand-white' : 'text-brand-gold/70'}>
              {selectedServiceType || 'Tipo de servicio'}
            </span>
            <FaChevronDown 
              className={`text-brand-gold transition-transform duration-200 ${
                isServiceDropdownOpen ? 'rotate-180' : ''
              }`} 
              size={14}
            />
          </button>
          
          {isServiceDropdownOpen && (
            <div className="dropdown-list">
              <div
                onClick={() => handleServiceTypeSelect('Venta')}
                className="dropdown-option"
              >
                Venta
              </div>
              <div
                onClick={() => handleServiceTypeSelect('Arriendo')}
                className="dropdown-option"
              >
                Arriendo
              </div>
            </div>
          )}
        </div>

        {/* Dropdown Tipo de Propiedad */}
        <div className="custom-dropdown relative md:col-span-1">
          <button
            type="button"
            onClick={togglePropertyDropdown}
            disabled={isLoading}
            className="w-full p-3 border-2 border-brand-gold rounded-lg bg-brand-black text-brand-white hover:border-brand-gold/80 focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-colors h-12 flex items-center justify-between text-left"
          >
            <span className={selectedPropertyType ? 'text-brand-white' : 'text-brand-gold/70'}>
              {isLoading 
                ? 'Cargando tipos...' 
                : selectedPropertyType || 'Tipo de propiedad'
              }
            </span>
            <FaChevronDown 
              className={`text-brand-gold transition-transform duration-200 ${
                isPropertyDropdownOpen ? 'rotate-180' : ''
              }`} 
              size={14}
            />
          </button>
          
          {isPropertyDropdownOpen && !isLoading && (
            <div className="dropdown-list">
              {propertyTypes.length > 0 ? (
                propertyTypes.map(type => (
                  <div
                    key={type}
                    onClick={() => handlePropertyTypeSelect(type)}
                    className="dropdown-option"
                  >
                    {type}
                  </div>
                ))
              ) : (
                <div className="dropdown-option text-brand-gold/50">
                  No hay tipos de propiedad disponibles
                </div>
              )}
            </div>
          )}
        </div>

        {/* Input de Ubicación */}

        <div className="custom-dropdown relative md:col-span-1">
          <button type="button" 
          onClick={toggleStateDropdown}
          disabled={isLoading}
          className="w-full p-3 border-2 border-brand-gold rounded-lg bg-brand-black text-brand-white hover:border-brand-gold/80 focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-colors h-12 flex items-center justify-between text-left">
          <span className={selectedState ? 'text-brand-white' : 'text-brand-gold/70'}>
            {isLoading 
              ? 'Cargando municipios...' 
              : (selectedState?.municipio ? selectedState.municipio : 'Municipio') 
            }
          </span>
          <FaChevronDown 
            className={`text-brand-gold transition-transform duration-200 ${
              isStateDropdownOpen ? 'rotate-180' : ''
            }`} 
            size={14}
          />
          </button>
          {isStateDropdownOpen && !isLoading && (
            <div className="dropdown-list">
              {states.length > 0 ? (
                states.map(state => (
                  <div key={state.codmunicipio} onClick={() => handleStateSelect(state)} className="dropdown-option">
                    {state.municipio.toUpperCase()}
                  </div>
                ))
              ) : (
                <div className="dropdown-option text-brand-gold/50">
                  No hay estados disponibles
                </div>
              )}
            </div>
          )}
        </div>

        <div className="custom-dropdown relative md:col-span-1">
          <button type="button" 
          onClick={toggleBarrioDropdown}
          className="w-full p-3 border-2 border-brand-gold rounded-lg bg-brand-black text-brand-white hover:border-brand-gold/80 focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-colors h-12 flex items-center justify-between text-left">
            <span className={selectedBarrio ? 'text-brand-white' : 'text-brand-gold/70'}>
              {isLoading 
                ? 'Cargando barrios...' 
                : (selectedBarrio?.barrio ? selectedBarrio.barrio : 'Barrio') 
              }
            </span>
            <FaChevronDown 
              className={`text-brand-gold transition-transform duration-200 ${
                isBarrioDropdownOpen ? 'rotate-180' : ''
              }`} 
              size={14}
            />
          </button>
          {isBarrioDropdownOpen && (
            <div className="dropdown-list">
              {barrios.length > 0 ? (
                barrios.map(barrio => (
                  <div key={barrio.codbarrio} onClick={() => handleBarrioSelect(barrio)} className="dropdown-option">
                    {barrio.barrio}
                  </div>
                ))
              ) : (
                <div className="dropdown-option text-brand-gold/50">
                  No hay barrios disponibles
                </div>
              )}
            </div>
          )}
        </div>

        {/* NUEVO: Dropdown de Rango de Precio */}
        <div className="custom-dropdown relative md:col-span-1">
          <button type="button" 
          onClick={togglePriceDropdown}
          disabled={!selectedServiceType} // Se desactiva si no se ha elegido Venta/Arriendo
          className="w-full p-3 border-2 border-brand-gold rounded-lg bg-brand-black text-brand-white hover:border-brand-gold/80 focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-colors h-12 flex items-center justify-between text-left disabled:opacity-50 disabled:cursor-not-allowed">
            <span className={selectedPriceRange ? 'text-brand-white' : 'text-brand-gold/70'}>
              {!selectedServiceType 
                ? 'Elige servicio primero' 
                : (selectedPriceRange ? selectedPriceRange.label : 'Rango de Precio') 
              }
            </span>
            <FaChevronDown 
              className={`text-brand-gold transition-transform duration-200 ${
                isPriceDropdownOpen ? 'rotate-180' : ''
              }`} 
              size={14}
            />
          </button>
          {isPriceDropdownOpen && selectedServiceType && (
            <div className="dropdown-list">
                {getPriceRanges().map((range, index) => (
                  <div key={index} onClick={() => handlePriceSelect(range)} className="dropdown-option">
                    {range.label}
                  </div>
                ))}
            </div>
          )}
        </div>




        {/*<div className="custom-dropdown relative md:col-span-2">
          <div className="relative">
            <FaMapMarkerAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-gold" />
            <input
              type="text"
              value={locationInput}
              onChange={handleLocationInputChange}
              onFocus={() => locationInput && setIsLocationDropdownOpen(true)}
              placeholder="Busca por barrio o municipio"
              className="w-full p-3 pl-10 border-2 border-brand-gold rounded-lg bg-brand-black text-brand-white hover:border-brand-gold/80 focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-colors h-12"
            />
          </div>
          {isLocationDropdownOpen && filteredLocations.length > 0 && (
            <div className="dropdown-list">
              {filteredLocations.map(loc => (
                <div
                  key={loc.id}
                  onClick={() => handleLocationSelect(loc)}
                  className="dropdown-option"
                >
                  {loc.label}
                </div>
              ))}
            </div>
          )}
        </div>*/}

        {/* Botón de Búsqueda */}
        <button 
          onClick={handleSearch}
          className="bg-brand-gold text-brand-black p-3 rounded-lg flex items-center justify-center hover:bg-brand-white hover:text-brand-black transition-all duration-300 shadow-md hover:shadow-xl border-2 border-brand-gold h-12 md:col-span-1"
        >
          <FaSearch size={20} />
        </button>
      </div>
    </div>
  );
};

export default SearchBar;
